import React, { useEffect, useState } from "react";
import type { FormInstance, Rule } from "antd/es/form";
import { message, notification, Select, Upload } from "antd";
import { getAllCountries } from "app/shared/api/getCountries";
import { getAllCurrencies } from "app/shared/api/getCurrencies";
import { Currency } from "app/shared/model";
import { focusFirstInvalidField } from "app/shared/validation/focusFirstInvalidField";
import type { Centre } from "./actions";

type FormNamePath = Parameters<FormInstance["getFieldValue"]>[0];

const onlyDigits = (s?: string) => (s || "").replace(/\D+/g, "");
const toPrintFormat = (printAs?: "A4" | "Letter"): number => (printAs === "Letter" ? 2 : 1);

export const normalizeSwiftCode = (value?: string | null): string =>
  String(value ?? "")
    .replace(/\s+/g, "")
    .toUpperCase();

export const normalizeHexColor = (value?: string | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;
  const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;
  if (![3, 6].includes(withoutHash.length)) return undefined;
  if (!/^[0-9a-fA-F]+$/.test(withoutHash)) return undefined;
  return `#${withoutHash.toUpperCase()}`;
};
export const DEFAULT_BRAND_COLOR = "#F78222";

const sanitizeEmail = (value?: string | null): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};
const sanitizeString = (value?: string | null): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const preferString = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
};

export const extractImageSource = (value: any, fallbackContentType?: string | null): string | undefined => {
  if (value === null || value === undefined || typeof value === "boolean") return undefined;

  const ensureDataUrl = (input: string, contentType?: string | null): string | undefined => {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    if (/^data:/i.test(trimmed)) return trimmed;
    const mime = (contentType && contentType.trim()) || "image/png";
    return `data:${mime};base64,${trimmed}`;
  };

  if (typeof value === "string") return ensureDataUrl(value, fallbackContentType);
  if (typeof value === "object") {
    const image = preferString(
      value.image,
      value.signatureImage,
      value.signatureCode,
      value.logoImage,
      value.base64,
      value.value,
      value.url
    );
    const contentType = preferString(
      value.contentType,
      value.signatureContentType,
      value.logoContentType,
      value.mimeType,
      fallbackContentType ?? undefined
    );
    return image ? ensureDataUrl(image, contentType) : undefined;
  }
  return undefined;
};

const buildAddressPayload = (
  formAddress?: { [key: string]: any } | null,
  existingAddress?: { [key: string]: any } | null
) => {
  const source = formAddress ?? {};
  const fallback = existingAddress ?? {};

  const line1 = preferString(
    source.building,
    source.line1,
    fallback.building,
    fallback.line1
  );
  const line2 = preferString(
    source.street,
    source.line2,
    fallback.street,
    fallback.line2
  );
  const line3 = preferString(
    source.postalCode,
    source.line3,
    fallback.postalCode,
    fallback.line3
  );
  const line4 = preferString(
    source.city,
    source.line4,
    fallback.city,
    fallback.line4
  );
  const rawCountry = preferString(
    source.country,
    source.countryISOCode,
    fallback.country,
    fallback.countryISOCode
  );
  const normalizedIso =
    typeof rawCountry === "string"
      ? rawCountry.trim().toUpperCase()
      : "";
  const countryISOCode = /^[A-Z]{2}$/.test(normalizedIso)
    ? normalizedIso
    : undefined;

  const hasContent =
    Boolean(line1 || line2 || line3 || line4 || countryISOCode) ||
    Boolean(fallback?.id ?? source?.id);

  if (!hasContent) return undefined;

  return {
    id: fallback?.id ?? source?.id,
    line1: line1 ?? "",
    line2: line2 ?? "",
    line3: line3 ?? "",
    line4: line4 ?? "",
    countryISOCode,
  };
};

export const tryIso2 = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const str = String(value).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(str) ? str : undefined;
};

export const optionalAddressCountryFormProps = (
  form: FormInstance,
  addressPath: FormNamePath,
  existingAddress: any
): { dependencies: FormNamePath[]; rules: Rule[] } => {
  const prefix = Array.isArray(addressPath) ? addressPath : [addressPath];
  const dep = (field: string): FormNamePath => [...prefix, field] as FormNamePath;
  const dependencies: FormNamePath[] = [
    dep("building"),
    dep("street"),
    dep("postalCode"),
    dep("city"),
    dep("country"),
    dep("id"),
  ];
  return {
    dependencies,
    rules: [
      {
        validator: async (_: unknown, value: unknown) => {
          const v = form.getFieldValue(addressPath);
          const built = buildAddressPayload(v, existingAddress);
          if (built == null) {
            return Promise.resolve();
          }
          if (built.countryISOCode || tryIso2(value) || tryIso2(v?.country) || tryIso2(v?.countryISOCode)) {
            return Promise.resolve();
          }
          if (value == null || value === "" || (typeof value === "string" && !value.trim())) {
            return Promise.reject(new Error("Address country is required"));
          }
          return Promise.reject(new Error("Use ISO-2, e.g., GB"));
        },
      } as Rule,
    ],
  };
};

const showValidationNotification = (labels: string[]) => {
  if (!labels.length) return;

  notification.error({
    key: "centre-validation",
    message: "Please fix the following fields:",
    description: (
        <div style={{ textAlign: "left" }}>
          {labels.map((l) => (
              <div key={l}>• {l}</div>
          ))}
        </div>
    ),
    duration: 8,
    placement: "top",
  });
};

export type HandleFormValidationErrorOptions = {
  beforeFocus?: (name: FormNamePath) => void | Promise<void>;
};

export const handleFormValidationError = (
  e: any,
  form: FormInstance,
  options?: HandleFormValidationErrorOptions,
) => {
  const errorFields = e?.errorFields ?? [];
  if (!errorFields.length) {
    message.error("Failed to save centre");
    return;
  }

  const labels = errorFields
      .flatMap((f: any) => (Array.isArray(f.errors) ? f.errors : []))
      .map((msg: any) => String(msg ?? "").trim())
      .map((msg: string) => msg.replace(/^['"]|['"]$/g, ""))
      .map((msg: string) => {
        const m = /^(.+?)\s+is required$/i.exec(msg);
        return m ? m[1].trim() : msg;
      })
      .filter(Boolean)
      .filter((x: string, i: number, arr: string[]) => arr.indexOf(x) === i);

  showValidationNotification(labels);

  const first = errorFields[0];
  if (first?.name) {
    focusFirstInvalidField(form, first.name, {
      beforeFocus: options?.beforeFocus,
      delayMs: options?.beforeFocus ? 300 : 250,
    });
  }
};

const toImageObj = (dataUrl?: string) => {
  if (!dataUrl || typeof dataUrl !== "string") return undefined;
  const trimmed = dataUrl.trim();
  if (!trimmed) return undefined;
  const m = /^data:(.*?);base64,(.*)$/i.exec(trimmed);
  if (!m) return undefined;
  const contentType = m[1] || undefined;
  const base64 = (m[2] ?? "").trim();
  if (!base64) return undefined;
  const loweredType = (contentType ?? "").toLowerCase();
  const fileName = loweredType.includes("png")
    ? "image.png"
    : loweredType.includes("jpeg") || loweredType.includes("jpg")
      ? "image.jpg"
      : loweredType.includes("svg")
        ? "image.svg"
        : "image.bin";
  return { image: base64, contentType, fileName };
};

export const buildApiCentreBody = (v: any, editing?: Centre | null) => {
  const toImagePayload = (
    input: any,
    fallbackMeta?: { contentType?: string | null | undefined; fileName?: string | null | undefined },
  ) => {
    if (!input) return undefined;
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (!trimmed) return undefined;
      const dataUrlPayload = toImageObj(trimmed);
      if (dataUrlPayload) return dataUrlPayload;
      return {
        image: trimmed,
        contentType: fallbackMeta?.contentType ?? undefined,
        fileName: fallbackMeta?.fileName ?? undefined,
      };
    }
    if (typeof input === "object") {
      const image = preferString(
        input.image,
        input.signatureImage,
        input.signatureCode,
        input.logoImage,
        input.base64,
        input.value
      );
      if (!image) return undefined;

      if (typeof image === "string") {
        const dataUrlPayload = toImageObj(image);
        if (dataUrlPayload) {
          return {
            image: dataUrlPayload.image,
            contentType: preferString(
              dataUrlPayload.contentType,
              input.contentType,
              input.signatureContentType,
              input.mimeType,
              fallbackMeta?.contentType
            ),
            fileName: preferString(
              dataUrlPayload.fileName,
              input.fileName,
              input.signatureFileName,
              input.name,
              fallbackMeta?.fileName
            ),
          };
        }
      }

      return {
        image,
        contentType: preferString(
          input.contentType,
          input.signatureContentType,
          input.mimeType,
          fallbackMeta?.contentType
        ),
        fileName: preferString(
          input.fileName,
          input.signatureFileName,
          input.name,
          fallbackMeta?.fileName
        ),
      };
    }
    return undefined;
  };

  const buildContact = (
    formPerson: any,
    existingPerson: any,
    contactType: number
  ) => {
    if (
      !formPerson?.name &&
      !formPerson?.email &&
      !formPerson?.signatureCode &&
      !existingPerson?.name &&
      !existingPerson?.email &&
      !existingPerson?.signatureImage
    ) {
      return undefined;
    }

    const signatureCleared = formPerson?.signatureCode === null;

    let uploadedSignature: any = undefined;
    if (!signatureCleared && formPerson?.signatureCode) {
      uploadedSignature = toImagePayload(formPerson?.signatureCode, {
        contentType: existingPerson?.signatureContentType,
        fileName: existingPerson?.signatureFileName,
      });
    }

    let fallbackSignature: any = undefined;
    if (!signatureCleared) {
      if (uploadedSignature) {
        fallbackSignature = uploadedSignature;
      } else if (existingPerson) {
        if (existingPerson.signatureImage) {
          const parsed = toImageObj(existingPerson.signatureImage);
          if (parsed) {
            fallbackSignature = parsed;
          } else {
            fallbackSignature = {
              image: existingPerson.signatureImage,
              contentType: existingPerson.signatureContentType,
              fileName: existingPerson.signatureFileName,
            };
          }
        } else if (existingPerson.signatureCode) {
          const parsed = toImagePayload(existingPerson.signatureCode, {
            contentType: existingPerson?.signatureContentType,
            fileName: existingPerson?.signatureFileName,
          });
          if (parsed) {
            fallbackSignature = parsed;
          }
        } else {
          const existingSignature = toImagePayload(existingPerson, {
            contentType: existingPerson?.signatureContentType,
            fileName: existingPerson?.signatureFileName,
          });
          if (existingSignature) {
            fallbackSignature = existingSignature;
          }
        }
      }
    }

    const name =
      sanitizeString(formPerson?.name) ?? sanitizeString(existingPerson?.name);
    const email =
      sanitizeEmail(formPerson?.email) ?? sanitizeEmail(existingPerson?.email);

    const signatureImage = signatureCleared
      ? null
      : (fallbackSignature?.image ? String(fallbackSignature.image) : undefined);
    const signatureContentType = signatureCleared
      ? null
      : (fallbackSignature?.contentType ? String(fallbackSignature.contentType) : undefined);
    const signatureFileName = signatureCleared
      ? null
      : (fallbackSignature?.fileName ? String(fallbackSignature.fileName) : undefined);

    return {
      contactType,
      id: existingPerson?.id ?? formPerson?.id,
      name: name ?? "",
      email: email ?? undefined,
      signatureImage,
      signatureContentType,
      signatureFileName,
    };
  };

  const directorContact = buildContact(v?.centreDirector, editing?.centreDirector, 1);
  const dosContact = buildContact(v?.directorOfStudies, editing?.directorOfStudies, 2);
  const contacts = [directorContact, dosContact].filter(Boolean) as any[];

  const logoCleared = v?.logoCode === null;
  let logoPayload: any = null;

  if (logoCleared) {
    logoPayload = null;
  } else if (v?.logoCode) {
    const logoCodeValue = typeof v.logoCode === "string" ? v.logoCode.trim() : v.logoCode;

    if (!logoCodeValue || logoCodeValue === "") {
      logoPayload = null;
    } else if (typeof logoCodeValue === "string") {
      const dataUrlMatch = /^data:([^;]+);base64,(.+)$/i.exec(logoCodeValue);
      if (dataUrlMatch) {
        const [, mimeType, base64] = dataUrlMatch;
        const base64Data = base64.trim();
        if (base64Data) {
          const loweredType = (mimeType ?? "").toLowerCase();
          const fileName = loweredType.includes("png")
            ? "image.png"
            : loweredType.includes("jpeg") || loweredType.includes("jpg")
              ? "image.jpg"
              : loweredType.includes("svg")
                ? "image.svg"
                : loweredType.includes("gif")
                  ? "image.gif"
                  : "image.bin";
          logoPayload = {
            image: base64Data,
            contentType: mimeType || "image/png",
            fileName: fileName,
          };
        } else {
          logoPayload = null;
        }
      } else {
        const processed = toImagePayload(logoCodeValue, {
          contentType: (editing as any)?.logo?.contentType,
          fileName: (editing as any)?.logo?.fileName,
        });

        if (processed && processed.image) {
          logoPayload = {
            image: processed.image,
            contentType: processed.contentType || "image/png",
            fileName: processed.fileName || "image.png",
          };
        } else {
          logoPayload = {
            image: logoCodeValue,
            contentType: (editing as any)?.logo?.contentType || "image/png",
            fileName: (editing as any)?.logo?.fileName || "image.png",
          };
        }
      }
    } else {
      const processed = toImagePayload(logoCodeValue, {
        contentType: (editing as any)?.logo?.contentType,
        fileName: (editing as any)?.logo?.fileName,
      });

      if (processed && processed.image) {
        logoPayload = {
          image: processed.image,
          contentType: processed.contentType || "image/png",
          fileName: processed.fileName || "image.png",
        };
      } else {
        logoPayload = null;
      }
    }
  } else if (editing?.logo) {
    const processed = toImagePayload((editing as any)?.logo);
    if (processed && processed.image) {
      logoPayload = {
        image: processed.image,
        contentType: processed.contentType || "image/png",
        fileName: processed.fileName || "image.png",
      };
    } else {
      logoPayload = null;
    }
  }

  const centreAddress = buildAddressPayload(v?.centreAddress, editing?.centreAddress) ?? {
    id: editing?.centreAddress?.id ?? undefined,
    line1: "",
    line2: "",
    line3: "",
    line4: "",
    countryISOCode: undefined as string | undefined,
  };

  const payload: any = {
    name: String(v?.centreName || "").trim(),
    centreCode: String(v?.centreCode || "").trim(),
    countryCode: centreAddress.countryISOCode ?? "",
    currencyCode: String(v?.currency || "").toUpperCase(),
    isActive: !!v?.active,
    physicalCentre: !!v?.physicalCentre,
    printFormat: toPrintFormat(v?.printAs ?? "A4"),
    brandColorHex: normalizeHexColor(v?.brandColorHex) ?? DEFAULT_BRAND_COLOR,

    generalEmail:
      sanitizeEmail(v?.generalEmail) ??
      sanitizeEmail(editing?.generalEmail) ??
      null,
    accommodationEmail:
      sanitizeEmail(v?.accommodationEmail) ??
      sanitizeEmail(editing?.accommodationEmail) ??
      null,
    telephone:
      sanitizeString(v?.telephoneNo) ??
      sanitizeString(editing?.telephoneNo) ??
      null,
    transferEmergencyNo:
      sanitizeString(v?.transferEmergencyNo) ??
      sanitizeString(editing?.transferEmergencyNo) ??
      null,
    emergencyTelephone:
      sanitizeString(v?.emergencyTelephone) ??
      sanitizeString(v?.emergencyNo) ??
      sanitizeString(editing?.emergencyTelephone) ??
      null,
    schoolSponsorshipNumber:
      sanitizeString(v?.schoolSponsorshipNumber) ??
      sanitizeString(editing?.schoolSponsorshipNumber) ??
      null,

    address: centreAddress,

    vatNumber: v?.vatNumber || editing?.vatNumber || null,
    registrationNumber: v?.registrationNumber || editing?.registrationNumber || null,
    vatExemptionNumber: v?.vatExemptionNumber || editing?.vatExemptionNumber || null,
    chequePayableTo: v?.chequePayableTo || editing?.chequePayableTo || null,

    ...(() => {
      const biPctForApi = (
        formVal: any,
        editingVal: number | undefined | null,
        hasEditing: boolean
      ): number | null | undefined => {
        const isEmpty = formVal === null || formVal === undefined || formVal === "";
        if (isEmpty) {
          if (hasEditing && editingVal !== undefined && editingVal !== null) {
            return null;
          }
          return undefined;
        }
        const n = typeof formVal === "number" ? formVal : Number(String(formVal).trim());
        if (!Number.isFinite(n)) return undefined;
        return Math.max(0, Math.min(100, n));
      };
      const hasEditing = !!editing;
      const g = biPctForApi(v?.guarantees, editing?.guarantees, hasEditing);
      const ir = biPctForApi(v?.individualsRatio, editing?.individualsRatio, hasEditing);
      const sr = biPctForApi(v?.staffingRatio, editing?.staffingRatio, hasEditing);
      const eb = biPctForApi(v?.emptyBeds, editing?.emptyBeds, hasEditing);
      const out: Record<string, number | null> = {};
      if (g !== undefined) out.guarantees = g;
      if (ir !== undefined) out.individualsRatio = ir;
      if (sr !== undefined) out.staffingRatio = sr;
      if (eb !== undefined) out.emptyBeds = eb;
      return out;
    })(),

    contacts,

    bankDetails: {
      id:
        v?.bankDetails?.id ??
        editing?.bankDetails?.id ??
        undefined,
      beneficiaryName:
        v?.bankDetails?.beneficiaryName ??
        editing?.bankDetails?.beneficiaryName ??
        "",
      accountNumber:
        v?.bankDetails?.accountNo ??
        v?.bankDetails?.accountNumber ??
        editing?.bankDetails?.accountNo ??
        "",
      bankName: v?.bankDetails?.bankName ?? editing?.bankDetails?.bankName ?? "",
      iban: (v?.bankDetails?.iban ?? editing?.bankDetails?.iban ?? "").trim(),
      swiftCode: normalizeSwiftCode(
        v?.bankDetails?.swiftCode ?? editing?.bankDetails?.swiftCode ?? ""
      ),
      branchCode: v?.bankDetails?.branchCode ?? editing?.bankDetails?.branchCode ?? null,
      abaRoutingNo: onlyDigits(v?.bankDetails?.abaRoutingNo ?? editing?.bankDetails?.abaRoutingNo),
      achAba: onlyDigits(v?.bankDetails?.achAba ?? editing?.bankDetails?.achAba),
      intermediaryBankName:
        v?.intermediaryBankDetails?.bankName ??
        editing?.intermediaryBankDetails?.bankName ??
        null,
      intermediarySwiftCode: (() => {
        const normalized = normalizeSwiftCode(
          v?.intermediaryBankDetails?.swiftCode ??
            editing?.intermediaryBankDetails?.swiftCode ??
            ""
        );
        return normalized || null;
      })(),
      bankAddress: (() => {
        const addr = buildAddressPayload(v?.bankAddress, editing?.bankAddress);
        if (addr) return addr;
        const countryCode = String(v?.country || editing?.country || "").toUpperCase();
        return {
          id: editing?.bankAddress?.id ?? undefined,
          line1: "",
          line2: "",
          line3: "",
          line4: "",
          countryISOCode: /^[A-Z]{2}$/.test(countryCode) ? countryCode : undefined,
        };
      })(),
      beneficiaryBankAddress: (() => {
        const addr = buildAddressPayload(v?.beneficiaryBankAddress, editing?.beneficiaryBankAddress);
        if (addr) return addr;
        const countryCode = String(v?.country || editing?.country || "").toUpperCase();
        return {
          id: editing?.beneficiaryBankAddress?.id ?? undefined,
          line1: "",
          line2: "",
          line3: "",
          line4: "",
          countryISOCode: /^[A-Z]{2}$/.test(countryCode) ? countryCode : undefined,
        };
      })(),
      intermediaryBankAddress: (() => {
        const addr = buildAddressPayload(
          v?.intermediaryBankDetails?.address,
          editing?.intermediaryBankDetails?.address
        );
        if (addr) return addr;
        const countryCode = String(v?.country || editing?.country || "").toUpperCase();
        return {
          id: editing?.intermediaryBankDetails?.address?.id ?? undefined,
          line1: "",
          line2: "",
          line3: "",
          line4: "",
          countryISOCode: /^[A-Z]{2}$/.test(countryCode) ? countryCode : undefined,
        };
      })(),
    },
  };

  payload.logo = logoPayload;

  return payload;
};

export const deriveIsoValue = (...inputs: unknown[]): string | undefined => {
  for (const input of inputs) {
    const iso = tryIso2(input);
    if (iso) return iso;
  }
  return undefined;
};

const pickFirstString = (...inputs: unknown[]): string | undefined => {
  for (const input of inputs) {
    if (input === null || input === undefined) continue;
    const str = String(input).trim();
    if (str) return str;
  }
  return undefined;
};

export const normalizeAddressForForm = (address?: any): any => {
  if (!address || typeof address !== "object") return address;
  const iso = deriveIsoValue(
    address.country,
    address.countryISOCode,
    address.countryCode,
    address.iso,
    address.isoCode
  );
  return {
    ...address,
    country: iso ?? undefined,
    countryISOCode: iso ?? address.countryISOCode ?? undefined,
  };
};

export const getIsoFromCountryDto = (dto: any): string | undefined => {
  if (!dto || typeof dto !== "object") return undefined;
  const candidates = [
    dto.iso2,
    dto.isoCode2,
    dto.alpha2,
    dto.code,
    dto.id,
    dto.isoCode,
    dto.countryCode,
  ];
  for (const candidate of candidates) {
    const iso = tryIso2(candidate);
    if (iso) return iso;
  }
  return undefined;
};

export const CountrySelect: React.FC<{ value?: string; onChange?: (val: string) => void }> = ({ value, onChange }) => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAllCountries()
      .then((countries) => {
        if (!mounted) return;
        const opts = (countries || [])
          .map((c: any) => {
            const iso2 = getIsoFromCountryDto(c);
            return iso2 ? { value: iso2, label: c.name ?? iso2 } : null;
          })
          .filter(Boolean) as { value: string; label: string }[];
        setOptions(opts);
      })
      .catch(() => message.error('Failed to load countries'))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <Select
      loading={loading}
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select country"
      showSearch={{
        optionFilterProp: 'label',
        filterOption: (input, option) =>
          (option?.label as string)?.toLowerCase().includes(input.toLowerCase()),
      }}
    />
  );
};

export const CurrencySelect: React.FC<{ value?: string; onChange?: (val: string) => void }> = ({ value, onChange }) => {
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getAllCurrencies()
      .then((currencies: Currency[]) => {
        if (!mounted) return;
        const seen = new Set<string>();
        const next: { value: string; label: string }[] = [];
        (currencies || []).forEach((c) => {
          const rawCode = String(c.code || "").toUpperCase();
          let code = rawCode;
          if (!/^[A-Z]{3}$/.test(code) && c.name) {
            const match = /\(([A-Za-z]{3})\)/.exec(c.name);
            if (match) {
              code = match[1].toUpperCase();
            }
          }
          if (!/^[A-Z]{3}$/.test(code)) return;
          if (seen.has(code)) return;
          seen.add(code);
          const label = c.name ? `${c.name} (${code})` : code;
          next.push({ value: code, label });
        });
        setOptions(next);
      })
      .catch(() => message.error('Failed to load currencies'))
      .finally(() => setLoading(false));
    return () => { mounted = false };
  }, []);

  return (
    <Select
      loading={loading}
      value={value ? String(value).toUpperCase() : value}
      onChange={(v) => onChange?.(String(v).toUpperCase())}
      options={options}
      showSearch={{ optionFilterProp: 'label' }}
      placeholder="Select Currency"
      allowClear
    />
  );
};

export const validateMaxSize = (file: File) => {
  const isValid = file.size / 1024 / 1024 < import.meta.env.VITE_FILE_UPLOAD_MAX_SIZE_MB;
  if (!isValid) {
    message.error(`File must be smaller than ${import.meta.env.VITE_FILE_UPLOAD_MAX_SIZE_MB}MB`);
  }
  return isValid || Upload.LIST_IGNORE;
};

/** Keep data-URL image values; ignore Ant Design Upload fileList/onChange payloads. */
export const preserveUploadedImageValue = (incoming: unknown, current: unknown) => {
  if (typeof incoming === "string" || incoming === null) return incoming;
  if (typeof current === "string" || current === null) return current;
  return undefined;
};

export const phoneRule = {
  pattern: /^[+]?[\d\s().-]{7,20}$/,
  message: 'Enter a valid phone incl. area code',
};

export const emailRule = { type: 'email' as const, message: 'Enter a valid email' };

export const centreContactEmailRules: Rule[] = [
  emailRule,
  { max: 50, message: 'Email must be 50 characters or fewer.' },
];

export const schoolSponsorshipNumberRules: Rule[] = [
  { max: 10, message: 'School sponsorship number must be 10 characters or fewer.' },
];

export const bankNameRules: Rule[] = [
  { max: 100, message: 'Bank name must be 100 characters or fewer.' },
];

export const beneficiaryNameRules: Rule[] = [
  { max: 100, message: 'Beneficiary name must be 100 characters or fewer.' },
];

export const accountNumberRules: Rule[] = [
  { max: 50, message: 'Account number must be 50 characters or fewer.' },
];

const SWIFT_CODE_PATTERN = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/;

const swiftCodeValidator: Rule = {
  validator: async (_, value) => {
    const normalized = normalizeSwiftCode(value);
    if (!normalized) return;
    if (!SWIFT_CODE_PATTERN.test(normalized)) {
      throw new Error("SWIFT must be 8 or 11 characters");
    }
  },
};

export const swiftCodeRules: Rule[] = [
  swiftCodeValidator,
  { max: 100, message: "Swift code must be 100 characters or fewer." },
];

export const intermediarySwiftCodeRules: Rule[] = [
  swiftCodeValidator,
  { max: 100, message: "Swift code must be 100 characters or fewer." },
];

export const achAbaRules: Rule[] = [
  { max: 10, message: 'ACH ABA must be 10 characters or fewer.' },
  {
    pattern: /^\d*$/,
    message: 'ACH ABA must contain only digits',
  },
];

export const preventSpace = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

export { pickFirstString };